import { useState, useEffect } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import { CallOffOrderColor, QuotedColor } from '@ravago/shared/page-data/models/elements';

import { useSearchParams } from 'next/navigation';

import { useCallOffAgreementContext } from '../../../../context/call-off-agreement-context';
import { useShoppingCartContext } from '../../../../context/shopping-cart-context';
import { useFlyoutContext } from '../../../../hooks/use-flyout-context';
import { getDefaultSelectedOrderNow } from '../utils/default-selection-utils';

interface Props {
   callOffOrderColors: CallOffOrderColor[];
   quotedColors: QuotedColor[];
   usesAgreementApp: boolean;
}

export const useOrderNowState = ({ callOffOrderColors, quotedColors, usesAgreementApp }: Props) => {
   const searchParams = useSearchParams();
   const { accessToken } = useAuthentication();
   const { addToCart, isAddingToCart } = useShoppingCartContext();
   const { dispatch: flyoutDispatch } = useFlyoutContext();
   const { state: callOffAgreementState, dispatch: callOffAgreementDispatch } = useCallOffAgreementContext();

   const [selectedCustomerColor, setSelectedCustomerColor] = useState<CallOffOrderColor | QuotedColor | undefined>(() =>
      getDefaultSelectedOrderNow(searchParams, callOffOrderColors, quotedColors)
   );
   const [quantityAmount, setQuantityAmount] = useState<number | undefined>();
   const [selectedQuotationLineId, setSelectedQuotationLineId] = useState<string | undefined>();
   const [quoteFlyoutOpen, setQuoteFlyoutOpen] = useState(false);

   useEffect(() => {
      if (selectedCustomerColor && 'quotation' in selectedCustomerColor) {
         const quotationLines = selectedCustomerColor.quotation.lines;
         if (quotationLines.length > 0) {
            const bestPrice = quotationLines.find((quotationLine) => quotationLine.bestPrice) ?? quotationLines[0];
            setSelectedQuotationLineId(bestPrice.id);
            setQuantityAmount(undefined);
         }
      } else {
         setSelectedQuotationLineId(undefined);
      }
   }, [selectedCustomerColor]);

   const handleSetSelectedCustomerColor = (newValue: CallOffOrderColor | QuotedColor) => {
      setSelectedCustomerColor(newValue);
   };

   const handleAddToCart = () => {
      if (!selectedCustomerColor || !('quotation' in selectedCustomerColor) || !accessToken) return;
      const selectedLine = selectedCustomerColor.quotation.lines.find((line) => line.id === selectedQuotationLineId);
      if (!selectedLine) return;
      addToCart({ type: 'QUOTATION', id: selectedLine.id }, selectedLine.orderableProduct, selectedLine.packaging, selectedLine.quantity);
      setQuoteFlyoutOpen(false);
   };

   const handleContinueCallOff = (callOffOrderColor: CallOffOrderColor, quantity: number) => {
      if (callOffAgreementState.callOffId !== callOffOrderColor.recommendedCallOff.id) {
         callOffAgreementDispatch?.({ type: 'clear_call_off', value: {} });
      }
      callOffAgreementDispatch?.({ type: 'set_call_off_id', value: { callOffId: callOffOrderColor.recommendedCallOff.id } });
      callOffAgreementDispatch?.({ type: 'set_opened_from', value: { openedFrom: 'dynamic-product-block' } });
      callOffAgreementDispatch?.({
         type: 'set_call_off_quantity',
         value: { quantity: { amount: quantity, unitOfMeasure: callOffOrderColor.recommendedCallOff.unitPrice.unitOfMeasure } }
      });
      callOffAgreementDispatch?.({ type: 'set_uses_agreement_app', value: { usesAgreementApp } });
      flyoutDispatch?.({ type: 'open_flyout', value: { flyoutType: 'call-off-agreement' } });
   };

   return {
      selectedCustomerColor,
      quantityAmount,
      selectedQuotationLineId,
      quoteFlyoutOpen,
      isAddingToCart,
      setSelectedCustomerColor: handleSetSelectedCustomerColor,
      setQuantityAmount,
      setSelectedQuotationLineId,
      handleAddToCart,
      handleContinueCallOff,
      setQuoteFlyoutOpen
   };
};
